import {
  Box,
  Button as JoyButton,
  ButtonProps,
  IconButton as JoyIconButton,
  Tooltip,
  TooltipProps,
} from '@mui/joy';
import { ForwardedRef, forwardRef } from 'react';

import { Badges } from './Badges';
import { IBadgeItem } from './types';

export interface IButtonProps extends ButtonProps {
  text?: string | JSX.Element;
  icon?: JSX.Element;
  hide?: boolean;
  badges?: IBadgeItem[];
  tooltipProps?: TooltipProps;
}

interface IButtonGroupPositionProps {
  'data-first-child'?: string;
  'data-last-child'?: string;
}

export const Button = forwardRef<HTMLButtonElement, IButtonProps>(function Button(
  props: IButtonProps,
  ref: ForwardedRef<HTMLButtonElement>
) {
  const { badges, tooltipProps, hide, ...rest } = props;

  if (hide) {
    return null;
  }

  if (tooltipProps) {
    // Inside a Joy ButtonGroup the wrapper Box is the group's direct child, so it must carry the
    // position markers the group's `& > [data-first-child]` rules target (radius/separator/margin
    // CSS vars then inherit into the button). Otherwise it is styled as a middle child.
    const {
      'data-first-child': dataFirstChild,
      'data-last-child': dataLastChild,
      ...buttonProps
    } = rest as IButtonProps & IButtonGroupPositionProps;

    return (
      <Tooltip {...tooltipProps}>
        <Box
          sx={{ position: 'relative' }}
          data-first-child={dataFirstChild}
          data-last-child={dataLastChild}
        >
          {renderButton(buttonProps, ref)}
        </Box>
      </Tooltip>
    );
  }

  const button = renderButton(rest, ref);

  if (badges) {
    return (
      <Box sx={{ position: 'relative' }}>
        {button}

        <Badges items={badges}></Badges>
      </Box>
    );
  }

  return button;
});

const renderButton = (props: IButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
  const { text, icon, startDecorator, endDecorator, ...rest } = props;

  if (icon && !text) {
    return (
      <JoyIconButton ref={ref} {...rest}>
        {icon}
      </JoyIconButton>
    );
  }

  return (
    <JoyButton ref={ref} startDecorator={startDecorator} endDecorator={endDecorator} {...rest}>
      {text}
    </JoyButton>
  );
};
